/**
 * Monthly billing review: client access to review state, the period lifecycle,
 * and the server billingReview* endpoints.
 *
 * Review decisions live on billingData/{yyyy-mm}.billingReview and are written
 * only by the Cloud Functions (Firestore rules block client writes), so
 * everything here reads Firestore directly but mutates via HTTPS endpoints.
 */
import { doc, getDoc, getDocs, collection, onSnapshot } from 'firebase/firestore';
import { auth, db, functionsBaseUrl } from '../firebase';
import { getEffectiveTenantId, tenantPatientDoc } from './tenantContext';

export const REVIEW_STATUS = {
  BILL: 'bill',
  NO_BILL: 'no_bill',
  PENDING: 'pending_review',
};

export const REVIEW_STATUS_LABELS = {
  [REVIEW_STATUS.BILL]: 'Bill',
  [REVIEW_STATUS.NO_BILL]: 'No Bill',
  [REVIEW_STATUS.PENDING]: 'Pending Review',
};

/**
 * System-suggested billing status for a billing report row.
 * Active RPM at period end → Bill. Not active but with readings or minutes in
 * the period → Pending Review (a person must decide). Otherwise → No Bill.
 * @param {object} row - buildBillingReport row
 */
export function computeSuggestedStatus(row) {
  const status = String(row?.rpmProgramStatus || '').toLowerCase();
  const hasActivity =
    (Number(row?.careMinutes) || 0) > 0 ||
    (Number(row?.electronicReadingsQty) || 0) > 0 ||
    (Number(row?.manualReadingsQty) || 0) > 0;
  if (status === 'active') return REVIEW_STATUS.BILL;
  if (hasActivity) return REVIEW_STATUS.PENDING;
  return REVIEW_STATUS.NO_BILL;
}

/**
 * Fingerprint of the billing inputs a reviewer saw when confirming. If this
 * changes after confirmation (minutes, readings, status, provider, payor), the
 * saved review is stale and must be re-confirmed before finalization.
 * @param {object} row - buildBillingReport row
 */
export function computeSourceVersion(row) {
  return [
    Number(row?.careMinutes) || 0,
    Number(row?.electronicReadingsQty) || 0,
    Number(row?.manualReadingsQty) || 0,
    String(row?.rpmProgramStatus || ''),
    String(row?.providerName || ''),
    String(row?.insurancePayor || ''),
  ].join('|');
}

const REVIEW_READ_CONCURRENCY = 50;

/**
 * Batch-read saved billingReview state for a set of patients in one period.
 * @param {string[]} patientIds
 * @param {string} ym - 'yyyy-mm'
 * @returns {Promise<Map<string, object>>} patientId → billingReview (only set when present)
 */
export async function batchGetBillingReviews(patientIds, ym) {
  const result = new Map();
  if (!patientIds?.length || !ym) return result;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return result;

  const readOne = async (pid) => {
    try {
      const snap = await getDoc(tenantPatientDoc(tenantId, pid, 'billingData', ym));
      const review = snap.exists() ? snap.data().billingReview : null;
      if (review && review.status) result.set(pid, review);
    } catch {
      /* unreadable doc → treated as unreviewed */
    }
  };
  for (let i = 0; i < patientIds.length; i += REVIEW_READ_CONCURRENCY) {
    await Promise.all(patientIds.slice(i, i + REVIEW_READ_CONCURRENCY).map(readOne));
  }
  return result;
}

/**
 * Live-subscribe to the billing period lifecycle doc.
 * @param {string} ym
 * @param {(period: object|null) => void} callback - null until/unless the doc exists
 * @returns {() => void} unsubscribe
 */
export function subscribeBillingPeriod(ym, callback) {
  if (!ym) return () => {};
  let unsub = null;
  let cancelled = false;
  (async () => {
    const tenantId = await getEffectiveTenantId();
    if (!tenantId || cancelled) return;
    const ref = doc(db, 'tenants', tenantId, 'billingPeriods', ym);
    unsub = onSnapshot(
      ref,
      (snap) => callback(snap.exists() ? { id: snap.id, ...snap.data() } : null),
      () => callback(null),
    );
  })();
  return () => {
    cancelled = true;
    if (unsub) unsub();
  };
}

/** One-shot read of a billing period doc (null when the period was never touched). */
export async function getBillingPeriod(ym) {
  if (!ym) return null;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return null;
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'billingPeriods', ym));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/** Read a finalized snapshot manifest + its frozen rows. */
export async function getSnapshotWithRows(versionId) {
  if (!versionId) return null;
  const tenantId = await getEffectiveTenantId();
  if (!tenantId) return null;
  const manifestRef = doc(db, 'tenants', tenantId, 'billingReportSnapshots', versionId);
  const manifestSnap = await getDoc(manifestRef);
  if (!manifestSnap.exists()) return null;
  const rowsSnap = await getDocs(collection(manifestRef, 'rows'));
  return {
    manifest: { id: manifestSnap.id, ...manifestSnap.data() },
    rows: rowsSnap.docs.map((d) => d.data()),
  };
}

/* ----------------------------- HTTPS endpoints ---------------------------- */

async function postEndpoint(name, payload) {
  if (!functionsBaseUrl) throw new Error('Cloud Functions base URL is not configured.');
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in.');
  const token = await user.getIdToken();
  const res = await fetch(`${functionsBaseUrl}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(payload),
  });
  let json = {};
  try {
    json = await res.json();
  } catch {
    /* non-JSON error body */
  }
  if (!res.ok) {
    const err = new Error(json?.error?.message || `Request failed (${res.status}).`);
    err.blockers = json?.error?.blockers || null;
    err.status = res.status;
    throw err;
  }
  return json.result || {};
}

const DECISIONS_CHUNK = 190;

/** Server-authoritative complete-tenant billing candidate for one month. */
export function getBillingReviewCandidate(ym) {
  return postEndpoint('billingReviewGetCandidate', { ym });
}

/**
 * Save review decisions (chunked; the server audits each one).
 * @param {string} ym
 * @param {{patientId: string, status: string, suggestedStatus?: string, note?: string, sourceVersion?: string}[]} decisions
 */
export async function setBillingReviewStatuses(ym, decisions, generation = null) {
  let updated = 0;
  for (let i = 0; i < decisions.length; i += DECISIONS_CHUNK) {
    const result = await postEndpoint('billingReviewSetStatus', {
      ym,
      generation,
      decisions: decisions.slice(i, i + DECISIONS_CHUNK),
    });
    updated += result.updated || 0;
  }
  return { updated };
}

/**
 * Finalize a period: server re-validates every row, freezes the snapshot, and
 * soft-locks the period. Throws with `.blockers` populated on validation failure.
 * @param {string} ym
 * @param {number} generation - candidate generation being attested
 */
export function finalizeBillingPeriod(ym, generation) {
  return postEndpoint('billingReviewFinalize', { ym, expectedGeneration: generation, attestation: true });
}

/** Reopen a finalized period (finalizers only; reason required). */
export function reopenBillingPeriod(ym, reason) {
  return postEndpoint('billingReviewReopen', { ym, reason });
}
